import { WALLET_CURRENCY } from '@shared/constants/business/wallet';
import Decimal from 'decimal.js';
import { DateTime } from 'luxon';

type LuxonFormatToken =
    | 'MMMM yyyy'
    | 'MMM yyyy'
    | 'dd MMM yyyy'
    | 'd MMM yyyy'
    | 'd MMM yyyy, HH:mm'
    | 'd MMM yyyy HH:mm'
    | 'd MMMM yyyy'
    | 'yyyy'
    | 'MMMM'
    | 'MMM'
    | 'MM'
    | 'dd MMM'
    | 'd MMM'
    | 'EEE, dd MMM'
    | 'EEE d MMM'
    | 'EEE'
    | 'EEEE'
    | 'dd'
    | 'HH'
    | 'mm'
    | 'ss'
    | 'yyyy-MM-dd'
    | 'HH:mm'
    | 'HH:mm:ss'
    | 'yyyy-MM-dd HH:mm:ss'
    | 'dd/MM/yyyy HH:mm'
    | 'dd/MM/yyyy'
    | 'dd/MM/yyyy HH:mm:ss'
    | 'dd/MM/yyyy hh:mm a'
    | 'h:mm a'
    | 'date-time'
    | 'date-short'
    | 'time'
    | 'iso-date-time';

const FORMAT_MAPPING: Partial<Record<LuxonFormatToken, string>> = {
    'date-time': 'dd/MM/yyyy HH:mm:ss',
    'date-short': 'dd/MM/yyyy',
    time: 'HH:mm a',
    'iso-date-time': "yyyy-MM-dd'T'HH:mm:ss",
} as const;

export const LUXON_FORMAT_TOKENS = {
    'h:mm a': 'h:mm a',
    'HH:mm:ss': 'HH:mm:ss',
    'dd MMM': 'dd MMM',
    'EEE, dd MMM': 'EEE, dd MMM',
    'yyyy-MM-dd': 'yyyy-MM-dd',
    'yyyy-MM-dd HH:mm:ss': 'yyyy-MM-dd HH:mm:ss',
    'MMMM yyyy': 'MMMM yyyy',
    'dd/MM/yyyy': 'dd/MM/yyyy',
    'dd/MM/yyyy HH:mm': 'dd/MM/yyyy HH:mm',
    'dd/MM/yyyy HH:mm:ss': 'dd/MM/yyyy HH:mm:ss',
    'dd/MM/yyyy hh:mm a': 'dd/MM/yyyy hh:mm a',
} as const;

/**
 * Options shared by the date formatters. `zone` shows the instant in a named IANA zone (an
 * organisation's report zone, a schedule's zone) instead of the viewer's; leave it out for the
 * viewer's local time.
 */
export type DateFormatOptions = { zone?: string };

export function formatDateTime(
    dt: Date | null | undefined,
    dtFormat: LuxonFormatToken = 'dd/MM/yyyy HH:mm',
    options?: DateFormatOptions,
) {
    return dt ? DateTime.fromJSDate(dt, { zone: options?.zone }).toFormat(FORMAT_MAPPING[dtFormat] || dtFormat) : '';
}

export function formatISODateTime(
    isoDt: string | null | undefined,
    dtFormat: LuxonFormatToken = 'dd/MM/yyyy HH:mm',
    options?: DateFormatOptions,
) {
    return isoDt ? DateTime.fromISO(isoDt, { zone: options?.zone }).toFormat(dtFormat) : '';
}

export function formatISOTime(
    isoDt: string | null | undefined,
    dtFormat: LuxonFormatToken = 'HH:mm',
    options?: DateFormatOptions,
) {
    return isoDt ? DateTime.fromISO(isoDt, { zone: options?.zone }).toFormat(dtFormat) : '';
}

export function formatDate(
    dt: Date | null | undefined,
    dtFormat: LuxonFormatToken = 'dd/MM/yyyy',
    options?: DateFormatOptions,
) {
    return dt ? DateTime.fromJSDate(dt, { zone: options?.zone }).toFormat(FORMAT_MAPPING[dtFormat] || dtFormat) : '';
}

export function formatISODate(
    isoDt: string | null | undefined,
    dtFormat: LuxonFormatToken = 'dd/MM/yyyy',
    options?: DateFormatOptions,
) {
    return isoDt ? DateTime.fromISO(isoDt, { zone: options?.zone }).toFormat(dtFormat) : '';
}

export function formatDateToRelative(dt: Date | null | undefined) {
    if (!dt) {
        return '';
    }

    const formattedDate = DateTime.fromJSDate(dt);
    const { months, years } = DateTime.now().diff(formattedDate, ['years', 'months']);

    if (months < 1) {
        return formattedDate.toRelative({ locale: 'en' });
    }

    if (years < 1) {
        return formatDate(dt, 'dd MMM');
    }

    return formatDate(dt);
}

export function numberToMonth(month: number, monthFormat: LuxonFormatToken = 'MMMM') {
    return DateTime.fromObject({ year: 2000, month: month + 1 }).toFormat(monthFormat);
}

export function formatCurrency(
    value: Decimal | bigint | number | null | undefined,
    options?: { maximumFractionDigits: number },
) {
    if (value === null || value === undefined) {
        return '-';
    }

    return Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: options?.maximumFractionDigits,
    }).format(Decimal.isDecimal(value) ? (value.toString() as any) : value);
}

/**
 * Format a US dollar amount (AI and provider costs are billed in USD) as `US$1,234.50`. The `US$`
 * prefix keeps it distinct from the AUD `$` that `formatCurrency` renders; the number uses en-AU
 * grouping with at least two decimal places, up to `maximumFractionDigits`.
 */
export function formatUsd(
    value: Decimal | number | null | undefined,
    options?: { maximumFractionDigits?: number; signDisplay?: 'auto' | 'always' },
) {
    if (value === null || value === undefined) {
        return '-';
    }
    // A Decimal is formatted from its string so large balances keep every digit.
    const decimal = Decimal.isDecimal(value) ? value : new Decimal(value);
    if (decimal.isNaN()) {
        return '-';
    }
    const maximumFractionDigits = options?.maximumFractionDigits ?? 2;
    const amount = Intl.NumberFormat('en-AU', {
        minimumFractionDigits: Math.min(2, maximumFractionDigits),
        maximumFractionDigits,
    }).format(decimal.abs().toFixed() as Intl.StringNumericLiteral);
    const sign = decimal.isNegative() && !decimal.isZero() ? '-' : options?.signDisplay === 'always' ? '+' : '';
    return `${sign}US$${amount}`;
}

/**
 * Format an organisation wallet amount (balance, top-up or transaction) in `WALLET_CURRENCY`, named
 * by its code (`AUD 1,234.50`) so it cannot be mistaken for the US$ AI and provider costs shown
 * elsewhere. The number uses en-AU grouping with two decimal places.
 */
export function formatWalletAmount(
    value: Decimal | number | null | undefined,
    options?: { signDisplay?: 'auto' | 'always' },
) {
    if (value === null || value === undefined) {
        return '-';
    }
    // A Decimal is formatted from its string so large balances keep every digit.
    const decimal = Decimal.isDecimal(value) ? value : new Decimal(value);
    if (decimal.isNaN()) {
        return '-';
    }
    return Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: WALLET_CURRENCY,
        currencyDisplay: 'code',
        signDisplay: options?.signDisplay ?? 'auto',
    }).format(decimal.toFixed() as Intl.StringNumericLiteral);
}

/**
 * Convert a number of bytes into a human-readable string (e.g., KB, MB, GB)
 * @param bytes - The number of bytes
 * @returns A formatted string representing the size
 */
export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format a number to a string with a specified locale and maximum fraction digits
 * @param value - The number to format
 * @param options - The options for the formatting
 * @param options.maximumFractionDigits - The maximum number of fraction digits
 * @param options.locale - The locale to format the number in
 * @param options.emptyValue - The value to return if the number is null or undefined
 */
export function formatNumber(
    value: number | null | undefined,
    options?: { maximumFractionDigits?: number; locale?: string; emptyValue?: string },
) {
    const _options = { ...{ maximumFractionDigits: 2, locale: 'en-AU', emptyValue: '-' }, ...options };
    if (value === null || value === undefined) {
        return _options.emptyValue;
    }
    return Intl.NumberFormat(_options.locale, { maximumFractionDigits: _options.maximumFractionDigits }).format(value);
}

export function formatAddress(data: {
    addressLine1?: string | null;
    addressLine2?: string | null;
    suburb?: string | null;
    city?: string | null;
    state?: string | null;
    postcode?: string | null;
    country?: string | null;
}) {
    return [
        data.addressLine1,
        data.addressLine2,
        [data.suburb, data.city, data.state, data.postcode].filter(Boolean).join(' '),
        data.country,
    ]
        .filter(Boolean)
        .join(', ');
}
